import React, { useMemo, useState, useEffect } from 'react';
import apiService from '../../../services/apiService';
import {
  Modal,
  Button,
  Upload,
  Steps,
  Alert,
  Card,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Result,
  Tooltip,
  Form,
  Row,
  Col,
  message,
  notification,
  Checkbox,
} from 'antd';
import * as XLSX from 'xlsx';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useTranslation } from "react-i18next";

const { Text, Paragraph, Title } = Typography;

interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
}

interface PreviewItem {
  row: number;
  orderNumber?: string;
  customer: string;
  customerName: string;
  customerId?: string; // Customer ID (more reliable than name, prevents typos)
  warehouse: string;
  warehouseName: string;
  productSku: string;
  expectedQuantity: number;
  baseUnit: string;
  packagingUnit: string;
  expectedArrivalDate: string;
  expiryDate?: string;
  logisticsMethod?: string;
  trackingNumber?: string;
  shipperContactName?: string;
  shipperContactPhone?: string;
  cargoNotes?: string;
  lotNumber?: string;
  serialNumber?: string;
  currentLocation: string; // Required field - must specify location for inbound
  length?: string;
  width?: string;
  height?: string;
  weight?: string;
  remark?: string;
  status: 'valid' | 'warning' | 'error';
  errors: ValidationError[];
}

interface InboundBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onUploadComplete: (orders: any[]) => void;
}

const InboundBulkUploadModal: React.FC<InboundBulkUploadModalProps> = ({
  visible,
  onCancel,
  onUploadComplete,
}) => {
    const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [fieldConfigForm] = Form.useForm();

  // 字段說明（必填在前、可選可勾選，用於生成模板）
  const fieldDescriptions = [
    // 必填字段（與入庫訂單表單要求一致）
    { field: 'customerName', name: '客戶名稱', required: true, description: t('客戶的完整名稱（必須與系統中的客戶名稱完全一致）') },
    { field: 'expectedArrivalDate', name: '預期到貨日期', required: true, description: t('預計貨物到達日期（YYYY-MM-DD格式）') },
    { field: 'productSku', name: 'SKU ID', required: true, description: t('產品SKU編碼（必須與系統中的產品SKU完全一致，區分大小寫）') },
    { field: 'expectedQuantity', name: '預期數量', required: true, description: t('預期入庫的數量') },
    { field: 'currentLocation', name: '貨位', required: true, description: t('存放貨位位置（必填，必須與系統中的貨位編碼完全一致）') },
    // 推薦字段 - 客戶ID（比客戶名稱更可靠，避免因拼寫錯誤導致匹配失敗）
    { field: 'customerId', name: '客戶ID', required: false, description: t('客戶系統ID（推薦使用，比客戶名稱更可靠，可防止拼寫錯誤）') },
    // 可選字段
    { field: 'warehouseName', name: '倉庫名稱', required: false, description: t('倉庫的完整名稱') },
    { field: 'logisticsMethod', name: '物流方式', required: false, description: t('運輸方式（如：DHL、順豐速運）') },
    { field: 'shipperContactName', name: '發貨聯繫人', required: false, description: t('發貨方聯繫人姓名') },
    { field: 'shipperContactPhone', name: '發貨聯繫電話', required: false, description: t('發貨方聯繫電話') },
    { field: 'baseUnit', name: '基本單位', required: false, description: t('計量的基本單位（如：PCS、KG）') },
    // 可選字段
    { field: 'orderNumber', name: '訂單號', required: false, description: t('入庫訂單編號（系統自動生成，Excel中的訂單號會被忽略）') },
    { field: 'customer', name: '客戶', required: false, description: t('客戶的識別代碼或編號') },
    { field: 'warehouse', name: '倉庫', required: false, description: t('倉庫的識別代碼') },
    { field: 'packagingUnit', name: '包裝單位', required: false, description: t('包裝的單位（如：BOX、CASE）') },
    { field: 'expiryDate', name: '到期日期', required: false, description: t('產品到期日期（YYYY-MM-DD格式）') },
    { field: 'trackingNumber', name: '追蹤號碼', required: false, description: t('物流追蹤號碼') },
    { field: 'cargoNotes', name: '貨物備註', required: false, description: t('關於貨物的額外說明或備註') },
    { field: 'lotNumber', name: '批次號', required: false, description: t('產品批次號碼') },
    { field: 'serialNumber', name: '序號', required: false, description: t('產品序列號') },
    { field: 'length', name: '長度', required: false, description: t('產品長度（cm）') },
    { field: 'width', name: '寬度', required: false, description: t('產品寬度（cm）') },
    { field: 'height', name: '高度', required: false, description: t('產品高度（cm）') },
    { field: 'weight', name: '重量', required: false, description: t('產品重量（kg）') },
    { field: 'remark', name: '明細備註', required: false, description: t('產品明細備註') }
  ];

  // 可選字段勾選（必填固定、可選可配置）
  // 默認只勾選幾個常用的可選字段（customerId 推薦使用，比客戶名稱更可靠）
  const optionalDefaultKeys = useMemo(() => ['customerId', 'orderNumber', 'expiryDate', 'lotNumber', 'trackingNumber'], []);
  const [selectedOptionalKeys, setSelectedOptionalKeys] = useState<string[]>(optionalDefaultKeys);

  // 重置狀態
  const resetState = () => {
    setCurrentStep(0);
    setIsProcessing(false);
    setUploadProgress(0);
    setPreviewData([]);
    setValidationErrors([]);
    setUploadedFile(null);
    fieldConfigForm.resetFields();
  };

  // 當模態框打開時重置狀態
  useEffect(() => {
    if (visible) {
      resetState();
    }
  }, [visible]);

  // 下載模板（包含所有字段，必填在前）- Excel格式
  const downloadTemplate = () => {
    const required = fieldDescriptions.filter(f => f.required);
    const optional = fieldDescriptions.filter(f => !f.required);
    const selected = [...required, ...optional];

    // 構建示例數據 - 展示多產品訂單功能與空字段繼承
    // 訂單 IN-2025-001：3個產品（演示多產品合併 + 空字段繼承）
    // 注意：customerId 是推薦字段，比 customerName 更可靠，可以防止拼寫錯誤
    const example1: Record<string, string> = {
      orderNumber: 'IN-2025-001', customer: 'CUST001', customerName: '深圳科技有限公司', customerId: 'cm3abc123def', warehouse: 'WH001', warehouseName: '主倉庫',
      productSku: 'SKU001', expectedQuantity: '100', baseUnit: 'PCS', packagingUnit: 'BOX', expectedArrivalDate: '2025-01-15',
      expiryDate: '2026-01-15', logisticsMethod: 'DHL', trackingNumber: 'DHL1234567890', shipperContactName: '張先生', shipperContactPhone: '13800000001',
      cargoNotes: '電子產品批次', lotNumber: 'LOT20250115', serialNumber: '', currentLocation: 'A-01-01',
      length: '30', width: '20', height: '15', weight: '5.5', remark: '易碎品'
    };
    // 第2行：訂單級別字段留空，自動繼承第1行
    const example2: Record<string, string> = {
      orderNumber: '', customer: '', customerName: '', customerId: '', warehouse: '', warehouseName: '',
      productSku: 'SKU002', expectedQuantity: '200', baseUnit: '', packagingUnit: '', expectedArrivalDate: '',
      expiryDate: '2026-01-16', logisticsMethod: '', trackingNumber: '', shipperContactName: '', shipperContactPhone: '',
      cargoNotes: '', lotNumber: 'LOT20250115', serialNumber: '', currentLocation: 'A-01-02',
      length: '40', width: '25', height: '20', weight: '8.0', remark: '防潮'
    };
    // 第3行：訂單級別字段留空，自動繼承第1行
    const example3: Record<string, string> = {
      orderNumber: '', customer: '', customerName: '', customerId: '', warehouse: '', warehouseName: '',
      productSku: 'SKU003', expectedQuantity: '150', baseUnit: '', packagingUnit: '', expectedArrivalDate: '',
      expiryDate: '2026-01-17', logisticsMethod: '', trackingNumber: '', shipperContactName: '', shipperContactPhone: '',
      cargoNotes: '', lotNumber: 'LOT20250115', serialNumber: '', currentLocation: 'A-01-03',
      length: '35', width: '22', height: '18', weight: '6.8', remark: '常溫存放'
    };
    // 訂單 IN-2025-002：2個產品（演示第二張訂單）
    const example4: Record<string, string> = {
      orderNumber: 'IN-2025-002', customer: 'CUST002', customerName: '上海貿易公司', customerId: 'cm3xyz789ghi', warehouse: 'WH001', warehouseName: '主倉庫',
      productSku: 'SKU004', expectedQuantity: '50', baseUnit: 'BOX', packagingUnit: 'PALLET', expectedArrivalDate: '2025-01-16',
      expiryDate: '2026-02-01', logisticsMethod: '順豐速運', trackingNumber: 'SF9876543210', shipperContactName: '李女士', shipperContactPhone: '13900000002',
      cargoNotes: '機械配件', lotNumber: 'LOT20250116', serialNumber: '', currentLocation: 'B-02-01',
      length: '50', width: '30', height: '25', weight: '12.5', remark: '重物小心'
    };
    // 第5行：訂單級別字段留空，自動繼承第4行
    const example5: Record<string, string> = {
      orderNumber: '', customer: '', customerName: '', customerId: '', warehouse: '', warehouseName: '',
      productSku: 'SKU005', expectedQuantity: '80', baseUnit: '', packagingUnit: '', expectedArrivalDate: '',
      expiryDate: '2026-02-15', logisticsMethod: '', trackingNumber: '', shipperContactName: '', shipperContactPhone: '',
      cargoNotes: '', lotNumber: 'LOT20250116', serialNumber: '', currentLocation: 'B-02-02',
      length: '45', width: '28', height: '22', weight: '10.0', remark: '防塵'
    };

    // 構建Excel數據
    const headers = selected.map(f => f.name);
    const data = [
      headers,
      selected.map(f => example1[f.field] ?? ''),
      selected.map(f => example2[f.field] ?? ''),
      selected.map(f => example3[f.field] ?? ''),
      selected.map(f => example4[f.field] ?? ''),
      selected.map(f => example5[f.field] ?? '')
    ];

    // 創建工作簿和工作表
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '入庫訂單模板');

    // 設置列寬
    const colWidths = selected.map(() => ({ wch: 15 }));
    worksheet['!cols'] = colWidths;

    // 下載Excel文件
    XLSX.writeFile(workbook, '入庫訂單模板.xlsx');
    message.success(t('Excel模板文件下載成功'));
  };

  // 上傳配置
  const uploadProps = {
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file: UploadFile) => {
      setUploadedFile(file);
      processFile(file);
      return false;
    },
  };

  // 處理文件
  const processFile = async (file: UploadFile) => {
    setIsProcessing(true);
    setUploadProgress(0);

    try {
      const fileName = (file as any).name || '';
      const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      
      let parsedData: any[];
      
      if (isExcelFile) {
        // Excel 文件處理
        console.log('檢測到 Excel 文件，使用 XLSX 庫解析');
        const fileContent = await readFileAsArrayBuffer(file);
        setUploadProgress(30);
        parsedData = parseExcelContent(fileContent);
        setUploadProgress(60);
      } else {
        // CSV 文件處理
        console.log('檢測到 CSV 文件，使用文本解析');
        const fileContent = await readFileContent(file);
        setUploadProgress(30);
        parsedData = parseCSVContent(fileContent);
        setUploadProgress(60);
      }

      // 驗證數據
      const validatedData = validateParsedData(parsedData);
      setUploadProgress(90);

      // 生成驗證錯誤列表
      const errors = generateValidationErrors(validatedData);
      
      setPreviewData(validatedData);
      setValidationErrors(errors);
      setUploadProgress(100);
      
      setIsProcessing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('文件處理錯誤:', error);
      const errorMessage = error instanceof Error ? error.message : t('未知錯誤');

      // 提供更詳細的錯誤提示
      if (errorMessage.includes('格式不正確')) {
        message.error({
          content: t('文件格式錯誤！請確保：1) 文件包含標題行和數據行；2) 如果用Excel編輯過，請另存為CSV (UTF-8) 格式或直接上傳 .xlsx 文件'),
          duration: 6
        });
      } else {
        message.error(t('文件處理失敗：{{errorMessage}}', { errorMessage }));
      }
      
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // 讀取文件內容（文本格式 - 用於 CSV）
  const readFileContent = (file: UploadFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        let content = e.target?.result as string;
        
        // 移除 BOM 字符（UTF-8, UTF-16 LE, UTF-16 BE）
        content = content.replace(/^\uFEFF/, ''); // UTF-8 BOM
        content = content.replace(/^\uFFFE/, ''); // UTF-16 LE BOM
        
        console.log('CSV 文件前100個字符:', content.substring(0, 100));
        console.log('CSV 文件第一行:', content.split('\n')[0]);
        
        resolve(content);
      };
      reader.onerror = () => reject(new Error(t('文件讀取失敗')));
      // 嘗試多種編碼
      reader.readAsText(file as unknown as File, 'UTF-8');
    });
  };

  // 讀取文件內容（二進制格式 - 用於 Excel）
  const readFileAsArrayBuffer = (file: UploadFile): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as ArrayBuffer;
        resolve(content);
      };
      reader.onerror = () => reject(new Error(t('文件讀取失敗')));
      reader.readAsArrayBuffer(file as unknown as File);
    });
  };

  // 將 Excel 日期序列號轉換為 YYYY-MM-DD 格式
  const convertExcelDate = (value: any): string => {
    if (!value) return '';
    
    // 如果已經是 YYYY-MM-DD 格式，直接返回
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    
    // 如果是數字（Excel 日期序列號）
    if (typeof value === 'number') {
      // Excel 日期從 1900-01-01 開始計算（序列號 1）
      // JavaScript Date 從 1970-01-01 開始
      const excelEpoch = new Date(1899, 11, 30); // Excel 的基準日期
      const date = new Date(excelEpoch.getTime() + value * 86400000);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
    
    // 如果是 Date 對象
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
    
    // 嘗試解析其他日期格式（如 15/1/2025、2025/1/15、15-1-2025 等）
    const str = String(value).trim();
    
    // 匹配各種日期格式
    const patterns = [
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/,  // DD/MM/YYYY 或 MM/DD/YYYY
      /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/,  // YYYY/MM/DD
    ];
    
    for (const pattern of patterns) {
      const match = str.match(pattern);
      if (match) {
        let year, month, day;
        
        if (match[1].length === 4) {
          // YYYY/MM/DD 格式
          year = match[1];
          month = match[2].padStart(2, '0');
          day = match[3].padStart(2, '0');
        } else {
          // DD/MM/YYYY 格式（假設是日/月/年）
          day = match[1].padStart(2, '0');
          month = match[2].padStart(2, '0');
          year = match[3];
        }
        
        return `${year}-${month}-${day}`;
      }
    }
    
    // 如果無法解析，返回原值
    return str;
  };

  // 解析 Excel 內容
  const parseExcelContent = (arrayBuffer: ArrayBuffer): any[] => {
    try {
      // 讀取 Excel 文件（保留原始格式）
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      
      // 獲取第一個工作表
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // 轉換為 JSON 格式（保留標題行）
      // Fix for corrupted cells where display value differs from stored value
      // Extract correct value from rich text (r property) when available
      const fixCellValue = (cell: any): any => {
        if (!cell) return '';
        // If cell has rich text with different value than stored value, use rich text
        if (cell.r && typeof cell.r === 'string') {
          const match = cell.r.match(/<t>([^<]+)<\/t>/);
          if (match && match[1] !== cell.v) {
            return match[1];
          }
        }
        // For dates (type 'n'), return the raw numeric value so it can be properly converted
        // For other types, use formatted value (w) or raw value (v)
        if (cell.t === 'n' && cell.v !== undefined) {
          return cell.v; // Return numeric value for dates
        }
        return cell.w || cell.v || '';
      };

      // Get range of worksheet
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      const jsonData: any[][] = [];

      for (let row = range.s.r; row <= range.e.r; row++) {
        const rowData: any[] = [];
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          const cell = worksheet[cellAddress];
          rowData.push(fixCellValue(cell));
        }
        jsonData.push(rowData);
      }
      
      if (jsonData.length < 2) {
        throw new Error('Excel 文件格式不正確，至少需要標題行和一行數據');
      }
      
      // 第一行是標題
      const headers = jsonData[0].map((h: any) => String(h || '').trim());
      
      // 識別日期字段（根據字段名稱）
      const dateFields = ['expectedArrivalDate', 'expiryDate'];
      const dateFieldIndices = headers.map((header, index) => {
        const fieldKey = getFieldKey(header);
        return dateFields.includes(fieldKey) ? index : -1;
      }).filter(index => index !== -1);
      
      // 後續行是數據
      const data: any[] = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row: any = { row: i };
        const values = jsonData[i];
        
        headers.forEach((header, index) => {
          const fieldKey = getFieldKey(header);
          let value = values[index];
          
          // 如果是日期字段，進行格式轉換
          if (dateFieldIndices.includes(index)) {
            value = convertExcelDate(value);
          } else {
            value = String(value || '').trim();
          }
          
          row[fieldKey] = value;
        });
        
        data.push(row);
      }
      
      console.log(`成功解析 Excel 文件：${headers.length} 個欄位，${data.length} 行數據`);
      return data;
    } catch (error) {
      console.error('Excel 解析錯誤:', error);
      throw new Error(t('Excel 文件解析失敗：') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  // 自動檢測CSV分隔符
  const detectDelimiter = (content: string): string => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return ',';
    
    const firstLine = lines[0];
    const delimiters = [',', ';', '\t', '|'];
    const counts = delimiters.map(delim => ({
      delimiter: delim,
      count: firstLine.split(delim).length
    }));
    
    // 選擇分隔最多字段的分隔符
    counts.sort((a, b) => b.count - a.count);
    return counts[0].count > 1 ? counts[0].delimiter : ',';
  };

  // 解析CSV內容
  const parseCSVContent = (content: string): any[] => {
    // 移除BOM字符（多種BOM格式）
    content = content.replace(/^\uFEFF/, ''); // UTF-8 BOM
    content = content.replace(/^\uFFFE/, ''); // UTF-16 LE BOM
    content = content.replace(/^\ufeff/, ''); // 小寫版本
    
    const lines = content.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正確，至少需要標題行和一行數據');
    }

    // 自動檢測分隔符
    const delimiter = detectDelimiter(content);
    console.log('檢測到的CSV分隔符:', delimiter === ',' ? '逗號' : delimiter === ';' ? '分號' : delimiter === '\t' ? '制表符' : delimiter);

    const headers = lines[0].split(delimiter).map(header => header.trim().replace(/"/g, '').replace(/\r/g, ''));
    
    console.log('解析到的標題行:', headers);
    console.log('標題行映射結果:', headers.map(h => `"${h}" -> "${getFieldKey(h)}"`));
    
    // 識別日期字段（根據字段名稱）
    const dateFields = ['expectedArrivalDate', 'expiryDate'];
    const dateFieldIndices = headers.map((header, index) => {
      const fieldKey = getFieldKey(header);
      return dateFields.includes(fieldKey) ? index : -1;
    }).filter(index => index !== -1);
    
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      const row: any = { row: i };
      
      // 即使字段數量不匹配也嘗試解析，只是給出警告
      if (values.length !== headers.length) {
        console.warn(`第${i+1}行字段數量不匹配: 期望${headers.length}個字段，實際${values.length}個字段`);
      }
      
      // 按照標題數量進行映射，缺少的字段用空字符串填充
      headers.forEach((header, index) => {
        const fieldKey = getFieldKey(header);
        let value = (index < values.length ? values[index] : '').trim();
        
        // 如果是日期字段，進行格式轉換
        if (dateFieldIndices.includes(index) && value) {
          value = convertExcelDate(value);
        }
        
        row[fieldKey] = value;
      });
      
      // 調試：打印第一行數據的映射結果
      if (i === 1) {
        console.log('第一行數據解析結果:', row);
      }
      
      data.push(row);
    }

    console.log(`成功解析 CSV 文件：${headers.length} 個欄位，${data.length} 行數據`);
    return data;
  };

  // 解析CSV行 - 正確處理空字段（支持自定義分隔符）
  const parseCSVLine = (line: string, delimiter: string = ','): string[] => {
    // 移除行末的換行符
    line = line.replace(/\r?\n$/, '');
    
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i <= line.length) {
      const char = i < line.length ? line[i] : delimiter; // 在結尾添加虛擬分隔符來處理最後一個字段
      
      if (char === '"') {
        inQuotes = !inQuotes;
        // 不將引號添加到結果中
      } else if (char === delimiter && !inQuotes) {
        // 遇到分隔符，添加當前字段到結果（即使是空的）
        result.push(current.trim());
        current = '';
      } else if (i < line.length) {
        // 普通字符，添加到當前字段
        current += char;
      }
      
      i++;
    }
    
    return result;
  };

  // 字段映射
  const getFieldKey = (header: string): string => {
    // 清理標題字符串：移除所有不可見字符和多餘空格
    const cleanHeader = header
      .trim()
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // 移除零寬字符
      .replace(/\s+/g, '') // 移除所有空格
      .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, ''); // 只保留中文、英文和數字
    
    // 使用清理後的標題進行映射
    const fieldMap: Record<string, string> = {
      '訂單號': 'orderNumber',
      '订单号': 'orderNumber', // 簡體中文
      '客戶': 'customer',
      '客戶名稱': 'customerName',
      '客户名称': 'customerName', // 簡體中文
      '客戶ID': 'customerId',
      '客户ID': 'customerId', // 簡體中文
      'CustomerID': 'customerId', // 英文
      'customerId': 'customerId', // 英文小寫
      '倉庫': 'warehouse',
      '倉庫名稱': 'warehouseName',
      '仓库名称': 'warehouseName', // 簡體中文
      'SKUID': 'productSku',
      '預期數量': 'expectedQuantity',
      '预期数量': 'expectedQuantity', // 簡體中文
      '基本單位': 'baseUnit',
      '基本单位': 'baseUnit', // 簡體中文
      '包裝單位': 'packagingUnit',
      '包装单位': 'packagingUnit', // 簡體中文
      '預期到貨日期': 'expectedArrivalDate',
      '预期到货日期': 'expectedArrivalDate', // 簡體中文
      '到期日期': 'expiryDate',
      '物流方式': 'logisticsMethod',
      '追蹤號碼': 'trackingNumber',
      '追踪号码': 'trackingNumber', // 簡體中文
      '發貨聯繫人': 'shipperContactName',
      '发货联系人': 'shipperContactName', // 簡體中文
      '發貨聯繫電話': 'shipperContactPhone',
      '发货联系电话': 'shipperContactPhone', // 簡體中文
      '貨物備註': 'cargoNotes',
      '货物备注': 'cargoNotes', // 簡體中文
      '批次號': 'lotNumber',
      '批次号': 'lotNumber', // 簡體中文
      '序號': 'serialNumber',
      '序号': 'serialNumber', // 簡體中文
      '貨位': 'currentLocation',
      '货位': 'currentLocation', // 簡體中文
      '長度': 'length',
      '长度': 'length', // 簡體中文
      '寬度': 'width',
      '宽度': 'width', // 簡體中文
      '高度': 'height',
      '重量': 'weight',
      '明細備註': 'remark',
      '明细备注': 'remark' // 簡體中文
    };
    
    // 首先嘗試使用清理後的標題匹配
    if (fieldMap[cleanHeader]) {
      return fieldMap[cleanHeader];
    }
    
    // 如果清理後的標題沒有匹配，嘗試原始標題
    if (fieldMap[header.trim()]) {
      return fieldMap[header.trim()];
    }
    
    // 如果都沒有匹配，返回清理後的標題
    return cleanHeader || header;
  };

  // 應用空字段繼承邏輯（carry-forward）
  const applyCarryForward = (data: any[]): any[] => {
    // 定義訂單級別字段（這些字段可以從上一行繼承）
    const orderLevelFields = [
      'orderNumber',
      'customerName',
      'customer',
      'customerId',
      'warehouseName',
      'warehouse',
      'warehouseId',
      'expectedArrivalDate',
      'logisticsMethod',
      'trackingNumber',
      'shipperContactName',
      'shipperContactPhone',
      'cargoNotes',
      'baseUnit',
      'packagingUnit'
    ];

    const result: any[] = [];
    let lastOrderData: Record<string, any> = {};

    for (let i = 0; i < data.length; i++) {
      const row = { ...data[i] };

      // 對每個訂單級別字段，如果當前行為空，則從上一行繼承
      for (const field of orderLevelFields) {
        const currentValue = row[field];
        const isEmpty = currentValue === null || currentValue === undefined ||
                       (typeof currentValue === 'string' && currentValue.trim() === '');

        if (isEmpty && lastOrderData[field]) {
          // 繼承上一行的值
          row[field] = lastOrderData[field];
        } else if (!isEmpty) {
          // 更新最後已知的值
          lastOrderData[field] = currentValue;
        }
      }

      result.push(row);
    }

    return result;
  };

  // 驗證解析的數據
  const validateParsedData = (data: any[]): PreviewItem[] => {
    // 先應用空字段繼承邏輯
    const dataWithCarryForward = applyCarryForward(data);

    return dataWithCarryForward.map((row, index) => {
      const errors: ValidationError[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      // 必填字段驗證（與入庫訂單表單要求一致）
      const requiredFields = [
        { key: 'customerName', name: t('客戶名稱') },
        { key: 'expectedArrivalDate', name: t('預期到貨日期') },
        { key: 'productSku', name: t('SKU ID') },
        { key: 'expectedQuantity', name: t('預期數量') },
        { key: 'currentLocation', name: t('貨位') }
      ];

      requiredFields.forEach(field => {
        if (!row[field.key] || row[field.key].toString().trim() === '') {
          errors.push({
            row: index + 1,
            field: field.key,
            value: row[field.key],
            error: t('{{fieldName}}不能為空', { fieldName: field.name }),
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 數字字段驗證（可選字段）
      if (row.expectedQuantity && row.expectedQuantity.toString().trim() !== '' && isNaN(Number(row.expectedQuantity))) {
        errors.push({
          row: index + 1,
          field: 'expectedQuantity',
          value: row.expectedQuantity,
          error: t('預期數量必須為數字'),
          severity: 'error'
        });
        status = 'error';
      }

      // 日期格式驗證（可選字段）
      if (row.expectedArrivalDate && row.expectedArrivalDate.trim() !== '' && !isValidDate(row.expectedArrivalDate)) {
        errors.push({
          row: index + 1,
          field: 'expectedArrivalDate',
          value: row.expectedArrivalDate,
          error: t('日期格式不正確，應為YYYY-MM-DD格式'),
          severity: 'error'
        });
        status = 'error';
      }

      // 到期日期格式驗證（可選字段）
      if (row.expiryDate && row.expiryDate.trim() !== '' && !isValidDate(row.expiryDate)) {
        errors.push({
          row: index + 1,
          field: 'expiryDate',
          value: row.expiryDate,
          error: t('到期日期格式不正確，應為YYYY-MM-DD格式'),
          severity: 'error'
        });
        status = 'error';
      }

      return {
        row: index + 1,
        orderNumber: row.orderNumber || '',
        customer: row.customer || '',
        customerName: row.customerName || '',
        customerId: row.customerId || '', // Customer ID for more reliable matching
        warehouse: row.warehouse || '',
        warehouseName: row.warehouseName || '',
        productSku: row.productSku || '',
        expectedQuantity: Number(row.expectedQuantity) || 0,
        baseUnit: row.baseUnit || '',
        packagingUnit: row.packagingUnit || '',
        expectedArrivalDate: row.expectedArrivalDate || '',
        expiryDate: row.expiryDate || '',
        logisticsMethod: row.logisticsMethod || '',
        trackingNumber: row.trackingNumber || '',
        shipperContactName: row.shipperContactName || '',
        shipperContactPhone: row.shipperContactPhone || '',
        cargoNotes: row.cargoNotes || '',
        lotNumber: row.lotNumber || '',
        serialNumber: row.serialNumber || '',
        currentLocation: row.currentLocation || '',
        length: row.length || '',
        width: row.width || '',
        height: row.height || '',
        weight: row.weight || '',
        remark: row.remark || '',
        status,
        errors
      };
    });
  };

  // 驗證日期格式
  const isValidDate = (dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  // 生成驗證錯誤列表
  const generateValidationErrors = (data: PreviewItem[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    data.forEach(item => {
      errors.push(...item.errors);
    });
    return errors;
  };

  // 確認導入
  const confirmImport = async () => {
    const validData = previewData.filter(item => item.status === 'valid' || item.status === 'warning');
    if (validData.length === 0) {
      message.error(t('沒有有效的數據可以導入'));
      return;
    }

    // 【修復】一次導入的所有記錄使用同一個入庫單號（系統自動生成）
    const sharedOrderNumber = generateOrderNumber();
    
    // 【修復】使用第一條記錄的訂單級別字段，確保所有記錄的訂單級別字段一致
    const firstItem = validData[0];
    const sharedOrderLevelFields = {
      customerName: firstItem.customerName,
      customerId: firstItem.customerId || null, // Customer ID for more reliable matching
      warehouseName: firstItem.warehouseName,
      expectedArrivalDate: firstItem.expectedArrivalDate,
      logisticsMethod: firstItem.logisticsMethod,
      trackingNumber: firstItem.trackingNumber,
      shipperContactName: firstItem.shipperContactName,
      shipperContactPhone: firstItem.shipperContactPhone,
      cargoRemark: firstItem.cargoNotes
    };

    // 構建後端需要的格式
    const ordersPayload = validData.map(item => ({
      orderNumber: sharedOrderNumber, // 所有記錄使用同一個訂單號
      customerName: sharedOrderLevelFields.customerName, // 使用第一條記錄的客戶名稱
      customerId: sharedOrderLevelFields.customerId, // 使用第一條記錄的客戶ID（比名稱更可靠）
      warehouseName: sharedOrderLevelFields.warehouseName, // 使用第一條記錄的倉庫名稱
      productSku: item.productSku, // 產品級別字段保持各記錄不同
      status: 'new',
      expectedQuantity: item.expectedQuantity, // 產品級別字段保持各記錄不同
      baseUnit: item.baseUnit, // 產品級別字段保持各記錄不同
      packagingUnit: item.packagingUnit, // 產品級別字段保持各記錄不同
      expectedArrivalDate: sharedOrderLevelFields.expectedArrivalDate, // 使用第一條記錄的預期到貨日期
      expiryDate: item.expiryDate || null, // 產品級別字段保持各記錄不同
      logisticsMethod: sharedOrderLevelFields.logisticsMethod, // 使用第一條記錄的物流方式
      trackingNumber: sharedOrderLevelFields.trackingNumber, // 使用第一條記錄的追蹤號碼
      shipperContactName: sharedOrderLevelFields.shipperContactName, // 使用第一條記錄的發貨聯繫人
      shipperContactPhone: sharedOrderLevelFields.shipperContactPhone, // 使用第一條記錄的發貨聯繫電話
      cargoRemark: sharedOrderLevelFields.cargoRemark, // 使用第一條記錄的貨物備註
      // 新增字段（產品級別字段保持各記錄不同）
      lotNumber: item.lotNumber,
      serialNumber: item.serialNumber,
      currentLocation: item.currentLocation,
      length: item.length ? parseFloat(item.length) : null,
      width: item.width ? parseFloat(item.width) : null,
      height: item.height ? parseFloat(item.height) : null,
      weight: item.weight ? parseFloat(item.weight) : null,
      remark: item.remark
    }));

    try {
      const resp: any = await apiService.bulkCreateInboundOrders(ordersPayload);
      const created = resp?.data || resp?.created || [];
      onUploadComplete(created);
      notification.success({
        message: t('批量導入成功'),
        description: t('成功導入 {{count}} 個產品到入庫單 {{orderNumber}}', { count: validData.length, orderNumber: sharedOrderNumber }),
        placement: 'topRight',
      });
      setCurrentStep(2);
    } catch (e: any) {
      console.error('批量導入錯誤:', e);

      // 處理後端驗證錯誤（訂單數據不一致）
      if (e?.response?.data?.validationErrors) {
        const errors = e.response.data.validationErrors;
        const errorMsg = e.response.data.message || t('訂單數據驗證失敗');
        const hint = e.response.data.hint || '';

        notification.error({
          message: errorMsg,
          description: (
            <div>
              <p style={{ marginBottom: 8 }}>{hint}</p>
              <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                {errors.map((err: string, idx: number) => (
                  <li key={idx} style={{ color: '#ff4d4f' }}>{err}</li>
                ))}
              </ul>
            </div>
          ),
          placement: 'topRight',
          duration: 10,
          style: { width: 500 }
        });
      } else {
        // 通用錯誤處理
        const errorMsg = e?.response?.data?.message || e?.message || t('批量導入失敗');
        message.error(errorMsg);
      }
    }
  };

  // 生成訂單號
  const generateOrderNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `IB${year}${month}${day}${random}`;
  };

  // 表格列配置
  const previewColumns = [
    {
      title: t('行號'),
      dataIndex: 'row',
      width: 60,
      render: (row: number, record: PreviewItem) => (
        <Tag color={record.status === 'error' ? 'red' : record.status === 'warning' ? 'orange' : 'green'}>
          {row}
        </Tag>
      ),
    },
    {
      title: t('客戶'),
      dataIndex: 'customerName',
      width: 120,
    },
    {
      title: t('倉庫'),
      dataIndex: 'warehouseName',
      width: 100,
    },
    {
      title: t('預期數量'),
      dataIndex: 'expectedQuantity',
      width: 80,
    },
    {
      title: t('預期到貨日期'),
      dataIndex: 'expectedArrivalDate',
      width: 120,
    },
    {
      title: t('狀態'),
      dataIndex: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'error' ? 'red' : status === 'warning' ? 'orange' : 'green'}>
          {status === 'error' ? t('錯誤') : status === 'warning' ? t('警告') : t('有效')}
        </Tag>
      ),
    },
  ];

  const errorColumns = [
    {
      title: t('行號'),
      dataIndex: 'row',
      width: 60,
    },
    {
      title: t('字段'),
      dataIndex: 'field',
      width: 100,
    },
    {
      title: t('值'),
      dataIndex: 'value',
      width: 150,
      render: (value: any) => value || <Text type="secondary">{t('空值')}</Text>,
    },
    {
      title: t('錯誤信息'),
      dataIndex: 'error',
      render: (error: string, record: ValidationError) => (
        <Text type={record.severity === 'error' ? 'danger' : 'warning'}>
          {error}
        </Text>
      ),
    },
    {
      title: t('嚴重性'),
      dataIndex: 'severity',
      width: 80,
      render: (severity: string) => (
        <Tag color={severity === 'error' ? 'red' : 'orange'}>
          {severity === 'error' ? t('錯誤') : t('警告')}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: t('上傳文件'),
      description: t('選擇並上傳CSV/Excel文件'),
    },
    {
      title: t('數據預覽'),
      description: t('查看和驗證數據'),
    },
    {
      title: t('導入完成'),
      description: t('數據導入成功'),
    },
  ];

  return (
    <Modal
      title={t('批量上傳入庫訂單')}
      open={visible}
      onCancel={() => {
        resetState();
        onCancel();
      }}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

      {currentStep === 0 && (
        <div>
          <Alert
            message={t('批量上傳說明')}
            description={
              <div>
                <p>{t('現在支持直接上傳CSV/Excel文件，系統將自動解析和驗證數據。您也可以下載模板文件作為參考。')}</p>
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  <strong>{t('⚠️ 數據驗證：')}</strong>{t('導入前系統會驗證以下數據必須在系統中存在：')}
                                        </p>
                <ul style={{ margin: '4px 0 0 20px', color: '#666' }}>
                  <li><strong>{t('客戶')}</strong>{t('：使用「客戶ID」或「客戶名稱」驗證（推薦使用客戶ID）')}</li>
                  <li><strong>SKU</strong>{t('：必須與系統中的產品SKU完全一致（區分大小寫）')}</li>
                  <li><strong>{t('貨位')}</strong>{t('：必填，必須與系統中的貨位編碼完全一致')}</li>
                </ul>
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  <strong>{t('💡 多產品訂單支持：')}</strong>{t('相同的「訂單號」會自動合併為一張訂單，每個SKU作為獨立明細項。')}
                                        </p>
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  <strong>{t('✨ 空字段自動繼承：')}</strong>{t('同一訂單的多個產品行中，訂單級別字段（客戶、倉庫、物流等）可以留空，系統會自動從上一行繼承，無需重複輸入！')}
                                        </p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title={t('步驟 1: 下載模板')} style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                {t('請先下載入庫訂單模板文件，模板包含所有必需的字段和格式說明。支持Excel格式（.xlsx），可直接用Microsoft Excel或WPS編輯。')}
                                            </Paragraph>
              <div style={{
                backgroundColor: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px'
              }}>
                <Text strong style={{ color: '#0050b3' }}>{t('📦 模板示例說明')}</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li><strong>{t('訂單 IN-2025-001')}</strong>{t('：包含3個產品（SKU001、SKU002、SKU003）→ 演示多產品訂單')}</li>
                  <li><strong>{t('訂單 IN-2025-002')}</strong>{t('：包含2個產品（SKU004、SKU005）→ 演示第二張訂單')}</li>
                  <li><strong>{t('✨ 空字段自動繼承')}</strong>{t('：第2、3、5行的訂單級別字段留空，自動從上一行繼承')}</li>
                  <li>{t('下載後可直接上傳測試，或修改為您的實際數據')}</li>
                </ul>
              </div>
              <div style={{
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <Text strong style={{ color: '#389e0d' }}>{t('✓ 重要提示')}</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li>{t('必填字段：')}<strong>{t('客戶名稱')}</strong>、<strong>{t('預期到貨日期')}</strong>、<strong>{t('SKU ID')}</strong>、<strong>{t('預期數量')}</strong>、<strong>{t('貨位')}</strong></li>
                  <li><strong style={{ color: '#fa8c16' }}>{t('數據驗證（必須存在於系統中）：')}</strong></li>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                    <li><strong>{t('客戶')}</strong>{t('：推薦使用客戶ID（可防止拼寫錯誤）')}</li>
                    <li><strong>SKU</strong>{t('：必須與產品管理中的SKU完全一致')}</li>
                    <li><strong>{t('貨位')}</strong>{t('：必填，必須與倉庫管理中的貨位編碼完全一致')}</li>
                  </ul>
                  <li>{t('日期格式：YYYY-MM-DD（如：2025-01-15）')}</li>
                  <li>{t('數量字段：直接輸入數字（如：100）')}</li>
                  <li>{t('相同訂單號會自動合併為一張訂單')}</li>
                  <li>{t('Excel文件可直接用Microsoft Excel或WPS編輯，無需擔心編碼問題')}</li>
                </ul>
              </div>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadTemplate}
                size="large"
              >
                {t('下載Excel模板')}
                                            </Button>
            </Space>
          </Card>

          <Card title={t('步驟 2: 上傳文件')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                {t('填寫完成後，請上傳文件進行數據驗證和預覽。')}
                                            </Paragraph>
              {/*<Alert
                message="✅ 好消息：現在可以直接用 Excel 編輯並上傳了！"
                description={
                  <div>
                    <p><strong>推薦方式（最簡單）：</strong></p>
                    <ol style={{ marginBottom: 8, paddingLeft: 20 }}>
                      <li>用 <strong>Microsoft Excel</strong> 打開下載的模板</li>
                      <li>編輯數據（隨意添加、修改、刪除）</li>
                      <li>另存為 <strong>.xlsx</strong> 格式（Excel 工作簿）</li>
                      <li>直接上傳 .xlsx 文件 ✅</li>
                    </ol>
                    <p style={{ marginTop: 8, marginBottom: 0, color: '#666', fontSize: '12px' }}>
                      💡 提示：系統完全支持 .xlsx、.xls 和 .csv 格式。使用 .xlsx 格式最簡單，無需擔心編碼或分隔符問題！
                    </p>
                  </div>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />*/}
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  size="large"
                  loading={isProcessing}
                >
                  {isProcessing ? t('處理中...') : t('選擇文件上傳')}
                </Button>
              </Upload>
              {uploadedFile && (
                <div>
                  <Text>{t('已選擇文件:')} {uploadedFile.name}</Text>
                  {isProcessing && (
                    <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
                  )}
                </div>
              )}
            </Space>
          </Card>

          <Card title={t('字段說明（必填在前、可選可勾選，用於生成模板）')} style={{ marginTop: 16 }}>
            <Title level={5}>{t('必填字段')}</Title>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              {fieldDescriptions.filter(f => f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color="red">{t('必填')}</Tag>
                    <Text strong>{field.name}</Text>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666', marginLeft: 16 }}>
                    {field.description}
                  </div>
                </Col>
              ))}
            </Row>

            <Title level={5}>{t('可選字段（勾選後包含在模板中）')}</Title>
            <Row gutter={16}>
              {fieldDescriptions.filter(f => !f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space align="start">
                    <Checkbox
                      checked={selectedOptionalKeys.includes(field.field)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? Array.from(new Set([...selectedOptionalKeys, field.field]))
                          : selectedOptionalKeys.filter(k => k !== field.field);
                        setSelectedOptionalKeys(next);
                      }}
                    />
                    <div>
                      <Space>
                        <Tag>{t('可選')}</Tag>
                        <Text strong>{field.name}</Text>
                      </Space>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {field.description}
                      </div>
                    </div>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>


        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message={t('數據驗證完成，共 {{count}} 行數據', { count: previewData.length })}
            description={
              <Space>
                <span>{t('有效:')} {previewData.filter(item => item.status === 'valid').length}</span>
                <span>{t('警告:')} {previewData.filter(item => item.status === 'warning').length}</span>
                <span>{t('錯誤:')} {previewData.filter(item => item.status === 'error').length}</span>
                <span style={{ marginLeft: '20px', color: '#666' }}>
                  {t('| 按鈕狀態:')} {validationErrors.filter(e => e.severity === 'error').length > 0 || 
                             previewData.filter(item => item.status === 'valid' || item.status === 'warning').length === 0 
                             ? t('🚫 已禁用') : t('✅ 可用')}
                </span>
              </Space>
            }
            type={validationErrors.filter(e => e.severity === 'error').length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationErrors.length > 0 && (
            <Card 
              title={
                <Space>
                  <span>{t('驗證錯誤')}</span>
                  <Tag color="red">
                    {validationErrors.filter(e => e.severity === 'error').length} {t('個錯誤')}
                                                        </Tag>
                  <Tag color="orange">
                    {validationErrors.filter(e => e.severity === 'warning').length} {t('個警告')}
                                                        </Tag>
                </Space>
              } 
              style={{ marginBottom: 16 }}
            >
              {validationErrors.filter(e => e.severity === 'error').length > 0 && (
                <Alert
                  message={t('存在錯誤項目')}
                  description={t('請修正所有錯誤項目後才能進行導入。警告項目不會阻止導入，但建議檢查。')}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Table
                dataSource={validationErrors}
                columns={errorColumns}
                rowKey={(record, index) => `${record.row}-${record.field}-${index}`}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          )}

          <Card title={t('數據預覽')} extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>{t('重新上傳')}</Button>
              {(() => {
                const errorCount = validationErrors.filter(e => e.severity === 'error').length;
                const hasErrors = errorCount > 0;
                const validDataCount = previewData.filter(item => item.status === 'valid' || item.status === 'warning').length;
                const isDisabled = validDataCount === 0 || hasErrors;

                return (
                  <Tooltip
                    title={
                      hasErrors
                        ? t('存在 {{errorCount}} 個錯誤，請修正後重新上傳', { errorCount })
                        : validDataCount === 0
                        ? t('沒有有效的數據可以導入')
                        : t('確認導入數據')
                    }
                  >
                    <Button 
                      type={isDisabled ? 'default' : 'primary'}
                      onClick={isDisabled ? undefined : confirmImport}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? '#f5f5f5' : undefined,
                        borderColor: isDisabled ? '#d9d9d9' : undefined,
                        color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      {t('確認導入')}
                                                </Button>
                  </Tooltip>
                );
              })()}
            </Space>
          }>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey="row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => 
                record.status === 'error' ? 'row-error' : 
                record.status === 'warning' ? 'row-warning' : 'row-valid'
              }
            />
          </Card>
        </div>
      )}

      {currentStep === 2 && (
        <Result
          status="success"
          title={t('批量導入成功！')}
          subTitle={t('成功導入 {{count}} 個入庫訂單，列表已自動刷新', { count: previewData.filter(item => item.status === 'valid' || item.status === 'warning').length })}
          icon={<CheckCircleOutlined />}
          extra={[
            <Button 
              type="primary" 
              key="close"
              onClick={() => {
                resetState();
                onCancel();
              }}
            >
              {t('關閉並返回列表')}
                              </Button>,
            <Button 
              key="continue"
              onClick={() => {
                resetState();
              }}
            >
              {t('繼續上傳')}
                            </Button>
          ]}
        />
      )}

      <style>{`
        .row-error {
          background-color: #fff2f0 !important;
        }
        .row-warning {
          background-color: #fffbe6 !important;
        }
        .row-valid {
          background-color: #f6ffed !important;
        }
        
        /* 強制禁用按鈕樣式 */
        .ant-btn-primary[disabled], 
        .ant-btn-primary[disabled]:hover, 
        .ant-btn-primary[disabled]:focus, 
        .ant-btn-primary[disabled]:active {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          color: #666 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        
        /* 確保禁用狀態下的樣式覆蓋 */
        .ant-btn[disabled] {
          background-color: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.25) !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </Modal>
  );
};

export default InboundBulkUploadModal;
